import { gatewayServiceBaseUrl } from "lib/consts/arcGateway";
import { authedFetch } from "lib/utils/authedFetch";
import { isHttpSecure } from "lib/utils/utils";

export type AudioTrack = {
    id: string;
    name: string;
    filename: string;
    duration_ms: number;
    size_bytes: number;
    created_at: string;
};

export type MicrophoneVolumePayload = { volume: number; mute: boolean };
export type MicrophoneVolumeUpdate = Partial<MicrophoneVolumePayload>;

export const isMicrophoneVolumePayload = (value: unknown): value is MicrophoneVolumePayload => {
    if (!value || typeof value !== "object") return false;
    const payload = value as Partial<MicrophoneVolumePayload>;
    return (
        typeof payload.volume === "number" &&
        Number.isInteger(payload.volume) &&
        payload.volume >= 0 &&
        payload.volume <= 100 &&
        typeof payload.mute === "boolean"
    );
};

const isAudioTrack = (value: unknown): value is AudioTrack => {
    if (!value || typeof value !== "object") return false;
    const track = value as Partial<AudioTrack>;
    return (
        typeof track.id === "string" &&
        typeof track.name === "string" &&
        typeof track.filename === "string" &&
        typeof track.duration_ms === "number" &&
        Number.isFinite(track.duration_ms) &&
        typeof track.size_bytes === "number" &&
        Number.isFinite(track.size_bytes) &&
        typeof track.created_at === "string"
    );
};

const defaultHost =
    process.env.REACT_APP_SINGLE_ARC_HOSTNAME ||
    (typeof window !== "undefined" ? window.location.hostname : "");

function tracksBaseUrl(arcId?: string | null, proxyHost?: string | null): string | null {
    const host = arcId && proxyHost ? proxyHost : defaultHost;
    return gatewayServiceBaseUrl("audioTracks", arcId, host);
}

function requireBase(arcId?: string | null, proxyHost?: string | null): string {
    const base = tracksBaseUrl(arcId, proxyHost);
    if (!base) {
        // Direct fallback when gateway helper returns null (e.g. local HTTP ARC).
        const scheme = isHttpSecure() ? "https" : "http";
        return `${scheme}://${defaultHost}/api/speaker-tracks`;
    }
    return base.replace(/\/$/, "");
}

function microphoneVolumeUrl(arcId?: string | null, proxyHost?: string | null): string {
    // A secure page is fleet mode. Never let a transient missing fleet identity
    // fall through to the direct-ARC fallback in requireBase().
    if (arcId && !proxyHost?.trim()) {
        throw new Error("Fleet proxy host is unavailable");
    }
    if (!arcId && isHttpSecure()) {
        throw new Error("Fleet ARC is not selected");
    }
    return `${requireBase(arcId, proxyHost)}/microphone/volume`;
}

export async function getMicrophoneVolume(
    arcId?: string | null,
    proxyHost?: string | null,
): Promise<MicrophoneVolumePayload> {
    const res = await authedFetch(microphoneVolumeUrl(arcId, proxyHost), { cache: "no-store" });
    if (!res.ok) throw new Error(`Failed to load microphone volume (${res.status})`);
    const payload: unknown = await res.json();
    if (!isMicrophoneVolumePayload(payload)) throw new Error("Invalid microphone volume response");
    return payload;
}

export async function setMicrophoneSettings(
    settings: MicrophoneVolumeUpdate,
    arcId?: string | null,
    proxyHost?: string | null,
): Promise<MicrophoneVolumePayload> {
    if (settings.volume === undefined && settings.mute === undefined) {
        throw new TypeError("At least one microphone setting is required");
    }
    if (settings.volume !== undefined &&
        (!Number.isInteger(settings.volume) || settings.volume < 0 || settings.volume > 100)) {
        throw new RangeError("Microphone volume must be an integer from 0 to 100");
    }
    if (settings.mute !== undefined && typeof settings.mute !== "boolean") {
        throw new TypeError("Microphone mute must be a boolean");
    }
    const res = await authedFetch(microphoneVolumeUrl(arcId, proxyHost), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settings),
    });
    if (!res.ok) throw new Error(`Failed to save microphone volume (${res.status})`);
    const payload: unknown = await res.json();
    if (!isMicrophoneVolumePayload(payload)) throw new Error("Invalid microphone volume response");
    return payload;
}

export async function setMicrophoneVolume(
    volume: number,
    arcId?: string | null,
    proxyHost?: string | null,
): Promise<MicrophoneVolumePayload> {
    return setMicrophoneSettings({ volume }, arcId, proxyHost);
}

/** Subscribe to the ARC's current microphone playback volume over authenticated SSE. */
export function subscribeMicrophoneVolume(
    arcId: string | null | undefined,
    proxyHost: string | null | undefined,
    onVolume: (settings: MicrophoneVolumePayload) => void,
    onError: (error: unknown) => void = () => {},
): () => void {
    const url = `${microphoneVolumeUrl(arcId, proxyHost)}/events`;
    const controller = new AbortController();
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const scheduleReconnect = () => {
        if (controller.signal.aborted || reconnectTimer) return;
        reconnectTimer = setTimeout(() => {
            reconnectTimer = null;
            void connect();
        }, 2000);
    };

    const processEvent = (block: string) => {
        if (controller.signal.aborted) return;
        const data = block
            .split(/\r?\n/)
            .filter((line) => line.startsWith("data:"))
            .map((line) => line.slice(5).replace(/^ /, ""))
            .join("\n");
        if (!data) return;
        try {
            const payload: unknown = JSON.parse(data);
            if (!isMicrophoneVolumePayload(payload)) throw new Error("Invalid microphone volume event");
            if (!controller.signal.aborted) onVolume(payload);
        } catch (error) {
            if (!controller.signal.aborted) onError(error);
        }
    };

    const connect = async () => {
        try {
            const response = await authedFetch(url, {
                headers: { Accept: "text/event-stream" },
                cache: "no-store",
                signal: controller.signal,
            });
            if (controller.signal.aborted) return;
            if (!response.ok) throw new Error(`Microphone volume stream failed (${response.status})`);
            if (!response.body) throw new Error("Microphone volume stream is unavailable");

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            try {
                while (!controller.signal.aborted) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });
                    let boundary = buffer.search(/\r?\n\r?\n/);
                    while (boundary >= 0) {
                        const separator = buffer.slice(boundary).match(/^\r?\n\r?\n/)?.[0] ?? "\n\n";
                        processEvent(buffer.slice(0, boundary));
                        buffer = buffer.slice(boundary + separator.length);
                        boundary = buffer.search(/\r?\n\r?\n/);
                    }
                    if (buffer.length > 64 * 1024) {
                        throw new Error("Microphone volume event is too large");
                    }
                }
                buffer += decoder.decode();
                if (buffer.trim()) processEvent(buffer);
            } finally {
                reader.releaseLock();
            }
            scheduleReconnect();
        } catch (error) {
            if (controller.signal.aborted) return;
            onError(error);
            scheduleReconnect();
        }
    };

    void connect();
    return () => {
        controller.abort();
        if (reconnectTimer) clearTimeout(reconnectTimer);
    };
}

export async function listAudioTracks(
    arcId?: string | null,
    proxyHost?: string | null,
): Promise<AudioTrack[]> {
    const res = await authedFetch(`${requireBase(arcId, proxyHost)}/tracks`);
    if (!res.ok) {
        throw new Error(`Failed to list audio tracks (${res.status})`);
    }
    const data = await res.json();
    return Array.isArray(data?.tracks) ? data.tracks.filter(isAudioTrack) : [];
}

export async function uploadAudioTrack(
    file: File,
    name?: string,
    arcId?: string | null,
    proxyHost?: string | null,
): Promise<AudioTrack> {
    const fd = new FormData();
    fd.append("file", file);
    if (name?.trim()) fd.append("name", name.trim());
    const res = await authedFetch(`${requireBase(arcId, proxyHost)}/tracks`, {
        method: "POST",
        body: fd,
    });
    if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Failed to upload audio (${res.status})`);
    }
    const data = await res.json();
    if (!isAudioTrack(data?.track)) throw new Error("Upload response missing track");
    return data.track;
}

export async function deleteAudioTrack(
    trackId: string,
    arcId?: string | null,
    proxyHost?: string | null,
): Promise<void> {
    const res = await authedFetch(
        `${requireBase(arcId, proxyHost)}/tracks/${encodeURIComponent(trackId)}`,
        { method: "DELETE" },
    );
    if (!res.ok) {
        throw new Error(`Failed to delete audio track (${res.status})`);
    }
}

export async function previewAudioTrack(
    trackId: string,
    volume = 80,
    arcId?: string | null,
    proxyHost?: string | null,
): Promise<void> {
    const res = await authedFetch(`${requireBase(arcId, proxyHost)}/preview/play`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ track_id: trackId, volume }),
    });
    if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Failed to play audio (${res.status})`);
    }
}

export async function stopPreviewAudio(
    arcId?: string | null,
    proxyHost?: string | null,
): Promise<void> {
    const res = await authedFetch(`${requireBase(arcId, proxyHost)}/preview/stop`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
    });
    if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Failed to stop audio (${res.status})`);
    }
}

export async function startMissionAudio(
    trackId: string,
    intervalSec = 30,
    volume = 80,
    arcId?: string | null,
    proxyHost?: string | null,
): Promise<void> {
    const res = await authedFetch(`${requireBase(arcId, proxyHost)}/mission/play`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            track_id: trackId,
            interval_sec: intervalSec,
            volume,
        }),
    });
    if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Failed to start mission audio (${res.status})`);
    }
}

export async function stopMissionAudio(
    arcId?: string | null,
    proxyHost?: string | null,
): Promise<void> {
    const res = await authedFetch(`${requireBase(arcId, proxyHost)}/mission/stop`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
    });
    if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Failed to stop mission audio (${res.status})`);
    }
}
